// src/pages/StatsPage.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaEuroSign,
  FaShoppingCart,
  FaUsers,
  FaEye,
  FaBolt,
  FaBox,
  FaSyncAlt,
  FaExclamationTriangle,
  FaLock,
  FaTruck,
  FaUserShield,
  FaEnvelope,
  FaExternalLinkAlt,
  FaClock,
  FaMousePointer,
  FaBroom
} from "react-icons/fa";

import "../styles/StatsPage.css";

export default function StatsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [stats, setStats] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const API_URL = "https://reanimeitor.com/backend/stats/summary.php";
  const MARK_ABANDONED_URL = "https://reanimeitor.com/backend/tracking/checkout_mark_abandoned.php";
  const NEWSLETTER_LIST_URL = "https://reanimeitor.com/backend/stats/newsletter_list.php";

  const getStoredUser = () => {
    try {
      const raw = localStorage.getItem("userData");

      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);

      if (!parsed || !parsed.id) {
        return null;
      }

      return parsed;
    } catch (err) {
      return null;
    }
  };

  const isSysAdminLocal = () => {
    const user = getStoredUser();
    return String(user?.role || "").trim() === "sys_admin";
  };

  const formatMoney = (value) => {
    const number = Number(value || 0);

    return number.toLocaleString("es-ES", {
      style: "currency",
      currency: "EUR"
    });
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    return number.toLocaleString("es-ES");
  };

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    try {
      return new Date(value.replace(" ", "T")).toLocaleString("es-ES");
    } catch (err) {
      return value;
    }
  };

  const shortUrl = (url) => {
    if (!url) {
      return "—";
    }

    try {
      const parsed = new URL(url);
      return `${parsed.pathname}${parsed.search || ""}` || "/";
    } catch (err) {
      return url.length > 60 ? `${url.slice(0, 60)}...` : url;
    }
  };

  const loadStats = async (forceRefresh = false) => {
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const localUser = getStoredUser();

      const response = await axios.get(API_URL, {
        params: {
          user_id: localUser?.id || "",
          refresh: forceRefresh ? "1" : ""
        },
        withCredentials: true,
        timeout: 12000
      });

      if (response.data?.success) {
        setStats(response.data);
        setLastRefresh(new Date());
      } else {
        setError(response.data?.error || "No se pudieron cargar las estadísticas.");
      }
    } catch (err) {
      console.error("Error cargando estadísticas:", err);

      if (err?.response?.status === 401) {
        setError("No estás autenticado. Inicia sesión de nuevo.");
      } else if (err?.response?.status === 403) {
        setError("Acceso denegado. Solo los usuarios sys_admin pueden ver este panel.");
      } else {
        setError("Error de conexión cargando estadísticas.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const markAbandonedCheckouts = async () => {
    setCleaning(true);
    setNotice("");
    setError("");

    try {
      const localUser = getStoredUser();

      const response = await axios.get(MARK_ABANDONED_URL, {
        params: {
          user_id: localUser?.id || ""
        },
        withCredentials: true,
        timeout: 15000
      });

      if (response.data?.success) {
        const affected = response.data?.affected_rows || 0;

        setNotice(
          affected === 1
            ? "Se ha marcado 1 intento antiguo como abandonado."
            : `Se han marcado ${affected} intentos antiguos como abandonados.`
        );

        await loadStats(true);
      } else {
        setError(response.data?.error || "No se pudieron marcar los abandonados.");
      }
    } catch (err) {
      console.error("Error marcando abandonados:", err);

      if (err?.response?.status === 401) {
        setError("No estás autenticado. Inicia sesión de nuevo.");
      } else if (err?.response?.status === 403) {
        setError("Acceso denegado. Solo sys_admin puede limpiar intentos.");
      } else {
        setError("Error de conexión marcando intentos abandonados.");
      }
    } finally {
      setCleaning(false);
    }
  };

  const toggleNewsletterSubscribers = async () => {
    setNotice("");
    setError("");

    if (newsletterOpen) {
      setNewsletterOpen(false);
      return;
    }

    setNewsletterLoading(true);

    try {
      const localUser = getStoredUser();

      const response = await axios.get(NEWSLETTER_LIST_URL, {
        params: {
          user_id: localUser?.id || ""
        },
        withCredentials: true,
        timeout: 12000
      });

      if (response.data?.success) {
        setNewsletterSubscribers(response.data.subscribers || []);
        setNewsletterOpen(true);
      } else {
        setError(response.data?.error || "No se pudo cargar la lista de suscriptores.");
      }
    } catch (err) {
      console.error("Error cargando newsletter:", err);

      if (err?.response?.status === 401) {
        setError("No estás autenticado. Inicia sesión de nuevo.");
      } else if (err?.response?.status === 403) {
        setError("Acceso denegado. Solo sys_admin puede ver la newsletter.");
      } else {
        setError("Error de conexión cargando suscriptores.");
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  useEffect(() => {
    if (!isSysAdminLocal()) {
      setError("Acceso restringido. Solo los usuarios sys_admin pueden entrar en estadísticas.");
      setLoading(false);
      return;
    }

    loadStats(false);
  }, []);

  if (loading) {
    return (
      <main className="stats-page">
        <div className="stats-loading-card">
          <div className="stats-loader"></div>
          <h2>Cargando estadísticas privadas...</h2>
          <p>Conectando con los datos internos de Reanimeitor.</p>
        </div>
      </main>
    );
  }

  if (error && !stats) {
    return (
      <main className="stats-page">
        <section className="stats-error-card">
          <div className="stats-error-icon">
            <FaLock />
          </div>

          <h1>Panel privado</h1>
          <p>{error}</p>

          <div className="stats-error-actions">
            <button onClick={() => navigate("/account")} className="stats-action-btn">
              Volver a mi cuenta
            </button>

            <button onClick={() => loadStats(true)} className="stats-action-btn secondary">
              Reintentar
            </button>
          </div>
        </section>
      </main>
    );
  }

  const sales = stats?.sales || {};
  const users = stats?.users || {};
  const visits = stats?.visits || {};
  const realtime = stats?.realtime || {};
  const checkout = stats?.checkout || {};
  const stripe = stats?.stripe || {};
  const newsletter = stats?.newsletter || {};

  return (
    <main className="stats-page">
      <section className="stats-hero">
        <div className="stats-hero-copy">
          <span className="stats-kicker">
            <FaUserShield /> Panel privado sys_admin
          </span>

          <h1>Estadísticas Reanimeitor</h1>

          <p>
            Control interno de ventas, visitas, usuarios activos, pedidos, registros y procesos de compra.
          </p>

          <div className="stats-hero-meta">
            <span>
              <FaClock /> Actualizado: {lastRefresh ? lastRefresh.toLocaleTimeString("es-ES") : "—"}
            </span>

            <span>
              <FaClock /> Caché: {Number(stats?.cache_seconds || 600) / 60} min
            </span>

            <span>
              <FaBolt /> Activos ahora: {formatNumber(realtime.active_now)}
            </span>
          </div>
        </div>

        <div className="stats-hero-actions">
          <button
            className="stats-refresh-btn"
            onClick={() => loadStats(true)}
            disabled={refreshing || cleaning || newsletterLoading}
          >
            <FaSyncAlt className={refreshing ? "stats-spin" : ""} />
            {refreshing ? "Actualizando..." : "Actualizar"}
          </button>

          <button
            className="stats-account-btn"
            onClick={markAbandonedCheckouts}
            disabled={cleaning || refreshing || newsletterLoading}
          >
            <FaBroom className={cleaning ? "stats-spin" : ""} />
            {cleaning ? "Limpiando..." : "Marcar abandonados"}
          </button>

          <button className="stats-account-btn" onClick={() => navigate("/account")}>
            Mi cuenta
          </button>
        </div>
      </section>

      {(notice || error) && (
        <section className="stats-message-wrap">
          {notice && (
            <div className="stats-message success">
              {notice}
            </div>
          )}

          {error && (
            <div className="stats-message error">
              {error}
            </div>
          )}
        </section>
      )}

      <section className="stats-grid-main">
        <article className="stats-card highlight">
          <div className="stats-card-icon">
            <FaEuroSign />
          </div>

          <div>
            <span>Dinero total ganado</span>
            <strong>{formatMoney(sales.total_revenue)}</strong>
            <small>{formatNumber(sales.total_sales)} ventas registradas</small>
          </div>
        </article>

        <article className="stats-card">
          <div className="stats-card-icon">
            <FaShoppingCart />
          </div>

          <div>
            <span>Ventas este mes</span>
            <strong>{formatNumber(sales.month_sales)}</strong>
            <small>{formatMoney(sales.month_revenue)}</small>
          </div>
        </article>

        <article className="stats-card">
          <div className="stats-card-icon">
            <FaTruck />
          </div>

          <div>
            <span>Pendientes de envío</span>
            <strong>{formatNumber(sales.pending_shipping)}</strong>
            <small>Pedidos pagados sin enviar</small>
          </div>
        </article>

        <article className="stats-card">
          <div className="stats-card-icon">
            <FaEye />
          </div>

          <div>
            <span>Visitas hoy</span>
            <strong>{formatNumber(visits.today_visits)}</strong>
            <small>{formatNumber(visits.unique_sessions_today)} usuarios únicos aprox.</small>
          </div>
        </article>

        <article className="stats-card">
          <div className="stats-card-icon">
            <FaBolt />
          </div>

          <div>
            <span>Usuarios activos</span>
            <strong>{formatNumber(realtime.active_now)}</strong>
            <small>{formatNumber(realtime.active_last_5_min)} en últimos 5 min</small>
          </div>
        </article>

        <article className="stats-card">
          <div className="stats-card-icon">
            <FaUsers />
          </div>

          <div>
            <span>Usuarios registrados</span>
            <strong>{formatNumber(users.total_users)}</strong>
            <small>{formatNumber(users.month_users)} nuevos este mes</small>
          </div>
        </article>

        <article className="stats-card">
          <div className="stats-card-icon">
            <FaMousePointer />
          </div>

          <div>
            <span>Intentos de compra</span>
            <strong>{formatNumber(checkout.total_attempts)}</strong>
            <small>{formatNumber(checkout.abandoned)} abandonados</small>
          </div>
        </article>

        <article className="stats-card">
          <div className="stats-card-icon">
            <FaChartLine />
          </div>

          <div>
            <span>Conversión</span>
            <strong>{Number(checkout.conversion_rate || 0).toFixed(2)}%</strong>
            <small>{formatNumber(checkout.completed)} completados por tracking</small>
          </div>
        </article>
      </section>

      <section className="stats-panels-grid">
        <article className="stats-panel">
          <div className="stats-panel-header">
            <h2>
              <FaBox /> Últimos pedidos
            </h2>
            <span>{formatNumber(sales.total_sales)} total</span>
          </div>

          <div className="stats-table-wrap">
            {sales.latest_orders?.length > 0 ? (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Cliente</th>
                    <th>Pack</th>
                    <th>Total</th>
                    <th>Envío</th>
                  </tr>
                </thead>

                <tbody>
                  {sales.latest_orders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.order_number}</td>
                      <td>
                        <strong>{order.customer_name || "—"}</strong>
                        <small>{order.customer_email || "—"}</small>
                      </td>
                      <td>{order.pack_name}</td>
                      <td>{formatMoney(order.amount_eur)}</td>
                      <td>
                        <span className={`stats-badge ${order.shipping_status || "pending"}`}>
                          {order.shipping_status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="stats-empty">
                <FaExclamationTriangle />
                <p>No hay pedidos todavía.</p>
              </div>
            )}
          </div>
        </article>

        <article className="stats-panel">
          <div className="stats-panel-header">
            <h2>
              <FaMousePointer /> Intentos de compra
            </h2>
            <span>{formatNumber(checkout.in_progress)} en curso</span>
          </div>

          <div className="stats-table-wrap">
            {checkout.latest_attempts?.length > 0 ? (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Pack</th>
                    <th>Cliente</th>
                    <th>Paso</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>

                <tbody>
                  {checkout.latest_attempts.map((attempt) => (
                    <tr key={attempt.id}>
                      <td>{attempt.pack_name || "—"}</td>
                      <td>
                        <strong>{attempt.customer_name || "—"}</strong>
                        <small>{attempt.customer_email || "—"}</small>
                      </td>
                      <td>{attempt.step || "—"}</td>
                      <td>
                        <span className={`stats-badge ${attempt.status || "started"}`}>
                          {attempt.status || "—"}
                        </span>
                      </td>
                      <td>{formatDate(attempt.updated_at || attempt.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="stats-empty">
                <FaExclamationTriangle />
                <p>Aún no hay intentos de compra registrados.</p>
              </div>
            )}
          </div>
        </article>

        <article className="stats-panel">
          <div className="stats-panel-header">
            <h2>
              <FaBolt /> Usuarios activos
            </h2>
            <span>Datos cacheados</span>
          </div>

          <div className="stats-list">
            {realtime.active_pages?.length > 0 ? (
              realtime.active_pages.map((page, index) => (
                <div className="stats-list-row" key={`${page.current_url}-${index}`}>
                  <div>
                    <strong>{shortUrl(page.current_url)}</strong>
                    <small>Página activa</small>
                  </div>

                  <span>{formatNumber(page.active_users)}</span>
                </div>
              ))
            ) : (
              <div className="stats-empty">
                <p>No hay usuarios activos ahora mismo.</p>
              </div>
            )}
          </div>
        </article>

        <article className="stats-panel">
          <div className="stats-panel-header">
            <h2>
              <FaEye /> Páginas más visitadas
            </h2>
            <span>Últimos 7 días</span>
          </div>

          <div className="stats-list">
            {visits.most_visited_pages?.length > 0 ? (
              visits.most_visited_pages.map((page, index) => (
                <div className="stats-list-row" key={`${page.url}-${index}`}>
                  <div>
                    <strong>{shortUrl(page.url)}</strong>
                    <small>URL visitada</small>
                  </div>

                  <span>{formatNumber(page.views)}</span>
                </div>
              ))
            ) : (
              <div className="stats-empty">
                <p>Aún no hay visitas registradas.</p>
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="stats-bottom-grid">
        <article className="stats-mini-panel">
          <h3>
            <FaBox /> Pack más vendido
          </h3>

          {sales.top_pack ? (
            <>
              <strong>{sales.top_pack.pack_name}</strong>
              <span>
                {formatNumber(sales.top_pack.total_sales)} ventas ·{" "}
                {formatMoney(sales.top_pack.total_revenue)}
              </span>
            </>
          ) : (
            <span>Sin datos todavía</span>
          )}
        </article>

        <article className="stats-mini-panel newsletter-mini-panel">
          <h3>
            <FaEnvelope /> Newsletter
          </h3>

          <strong>
            {newsletter.total === null ? "—" : formatNumber(newsletter.total)}
          </strong>

          <span>
            {newsletter.table_used
              ? `Tabla detectada: ${newsletter.table_used}`
              : "Tabla de newsletter no detectada todavía"}
          </span>

          <button
            type="button"
            className="stats-mini-action-btn"
            onClick={toggleNewsletterSubscribers}
            disabled={newsletterLoading}
          >
            {newsletterLoading ? "Cargando..." : newsletterOpen ? "Ocultar" : "Ver"}
          </button>
        </article>

        <article className="stats-mini-panel">
          <h3>
            <FaExternalLinkAlt /> Stripe sessions
          </h3>

          <strong>{formatNumber(stripe.total_sessions)}</strong>

          <span>
            {formatNumber(stripe.completed)} completadas · {formatNumber(stripe.pending)} pendientes
          </span>
        </article>
      </section>

      {newsletterOpen && (
        <section className="stats-panel stats-newsletter-panel">
          <div className="stats-panel-header">
            <h2>
              <FaEnvelope /> Usuarios suscritos a la newsletter
            </h2>

            <span>{formatNumber(newsletterSubscribers.length)} visibles</span>
          </div>

          <div className="stats-table-wrap">
            {newsletterSubscribers.length > 0 ? (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Nombre</th>
                    <th>Fecha</th>
                    <th>Fuente</th>
                    <th>Activo</th>
                    <th>Confirmado</th>
                  </tr>
                </thead>

                <tbody>
                  {newsletterSubscribers.map((subscriber) => (
                    <tr key={subscriber.id}>
                      <td>{subscriber.email || "—"}</td>
                      <td>{subscriber.nombre || "—"}</td>
                      <td>{formatDate(subscriber.fecha_suscripcion)}</td>
                      <td>{subscriber.fuente || "—"}</td>
                      <td>
                        <span className={`stats-badge ${subscriber.activo ? "completed" : "pending"}`}>
                          {subscriber.activo ? "Sí" : "No"}
                        </span>
                      </td>
                      <td>
                        <span className={`stats-badge ${subscriber.confirmado ? "completed" : "pending"}`}>
                          {subscriber.confirmado ? "Sí" : "No"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="stats-empty">
                <FaExclamationTriangle />
                <p>No hay suscriptores registrados.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </main>
  );
}